import PropTypes from 'prop-types';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Activity,
  CircleDot,
} from 'lucide-react';
import { Heading, Text, Stack } from '@/components/ui/index.js';
import styles from './EventCard.module.css';

// Sport icon mapping
const sportIcons = {
  basketball: '🏀',
  soccer: '⚽',
  tennis: '🎾',
  running: '🏃',
  swimming: '🏊',
  cycling: '🚴',
  volleyball: '🏐',
  yoga: '🧘',
  gym: '💪',
  hiking: '🥾',
  default: '⚡',
};

// Skill level colors
const skillLevelColors = {
  beginner: styles.skillBeginner,
  intermediate: styles.skillIntermediate,
  advanced: styles.skillAdvanced,
};

// Skill level labels
const skillLevelLabels = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

/**
 * EventCard - Individual event card with glassmorphism styling
 * Displays event details with join CTA and live indicator
 */
export function EventCard({ event, viewMode, onJoin }) {
  const isFull = event.participants >= event.maxParticipants;
  const sportIcon = sportIcons[event.sport] || sportIcons.default;
  const skillColor = skillLevelColors[event.skillLevel] || '';
  const skillLabel = skillLevelLabels[event.skillLevel] || event.skillLevel;

  return (
    <article
      className={`${styles.card} ${viewMode === 'list' ? styles.listView : ''}`}
      style={{ animationDelay: `${event.animationDelay || 0}ms` }}
    >
      {/* Live indicator */}
      {event.isLive && (
        <div className={styles.liveBadge}>
          <CircleDot size={8} className={styles.liveIcon} />
          <Text size="xs" weight="semibold" color="primary">
            LIVE
          </Text>
        </div>
      )}

      <div className={styles.cardContent}>
        {/* Sport icon & title */}
        <Stack direction="row" gap={3} align="flex-start">
          <div className={styles.sportIcon}>{sportIcon}</div>
          <div className={styles.titleSection}>
            <Heading level={3} size="sm" color="primary" className={styles.title}>
              {event.title}
            </Heading>
            <span className={`${styles.skillBadge} ${skillColor}`}>
              <Activity size={10} />
              <Text size="xs" color="primary">
                {skillLabel}
              </Text>
            </span>
          </div>
        </Stack>

        {/* Event details */}
        <Stack direction="column" gap={1} className={styles.details}>
          <Stack direction="row" gap={2} align="center" className={styles.detailItem}>
            <Calendar size={14} className={styles.detailIcon} />
            <Text size="sm" color="secondary">
              {event.date}
            </Text>
            <span className={styles.detailSeparator}>•</span>
            <Clock size={14} className={styles.detailIcon} />
            <Text size="sm" color="secondary">
              {event.time}
            </Text>
          </Stack>

          <Stack direction="row" gap={2} align="center" className={styles.detailItem}>
            <MapPin size={14} className={styles.detailIcon} />
            <Text size="sm" color="secondary" className={styles.location}>
              {event.location}
            </Text>
            <span className={styles.distance}>{event.distance} mi</span>
          </Stack>

          <Stack direction="row" gap={2} align="center" className={styles.detailItem}>
            <Users size={14} className={styles.detailIcon} />
            <Text size="sm" color="secondary">
              <span className={event.participants >= event.maxParticipants ? styles.fullText : ''}>
                {event.participants}/{event.maxParticipants}
              </span>{' '}
              participants
            </Text>
          </Stack>
        </Stack>

        {/* Join button */}
        <button
          type="button"
          className={`${styles.joinButton} ${isFull ? styles.full : ''}`}
          onClick={() => !isFull && onJoin(event.id)}
          disabled={isFull}
        >
          {isFull ? 'Full' : 'Join Event'}
        </button>
      </div>
    </article>
  );
}

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired,
    skillLevel: PropTypes.oneOf(['beginner', 'intermediate', 'advanced']).isRequired,
    participants: PropTypes.number.isRequired,
    maxParticipants: PropTypes.number.isRequired,
    isLive: PropTypes.bool,
    distance: PropTypes.number.isRequired,
    animationDelay: PropTypes.number,
  }).isRequired,
  viewMode: PropTypes.oneOf(['grid', 'list']).isRequired,
  onJoin: PropTypes.func.isRequired,
};

export default EventCard;
